import { cva, VariantProps } from 'class-variance-authority';
import React from 'react';

import { cn } from '@/utils';

const chipClass = cn(
	'rounded-[4px] flex-inline w-fit',
	'bg-brand-gray-5 text-brand-dark',
	'border border-brand-gray-4',
	'data-[disabled=true]:border-none data-[disabled=true]:text-brand-gray-2',
	'text-sm leading-tight font-normal font-sans',
	'focus:outline-none focus:ring-2 focus:ring-brand-blue focus:ring-offset-2',
	'selection:text-brand-blue'
);

const chipVariants = cva(chipClass, {
	variants: {
		size: {
			default: 'px-2 py-1',
			sm: 'px-1 py-0.5',
		},
	},
	defaultVariants: {
		size: 'default',
	},
});

export interface ChipProps extends React.HTMLAttributes<HTMLDivElement>, VariantProps<typeof chipVariants> {
	disabled?: boolean;
	onRemove?: React.MouseEventHandler<HTMLButtonElement>;
}

const Chip = React.forwardRef<HTMLDivElement, ChipProps>((props, ref) => {
	const { size, children, className, disabled, onRemove, ...restProps } = props;

	return (
		<div
			ref={ref}
			data-disabled={disabled}
			className={cn(chipVariants({ size, className }), {
				'cursor-pointer hover:bg-brand-gray-4 data-[disabled=true]:cursor-not-allowed': !!restProps.onClick,
			})}
			{...restProps}
		>
			{children}
			{onRemove && (
				<button
					type="button"
					onClick={onRemove}
					className="ml-1 text-brand-blue cursor-pointer disabled:text-brand-gray disabled:cursor-not-allowed"
					disabled={disabled}
				>
					✕
				</button>
			)}
		</div>
	);
});

export { Chip, chipVariants };
